export class InputManager {
  readonly keys = new Set<string>();
  mouseDX = 0;
  mouseDY = 0;
  pointerLocked = false;
  interactPressed = false;
  usePressed = false;
  pausePressed = false;
  inventoryPressed = false;
  hintPressed = false;

  /** モバイルジョイスティック -1..1 */
  joyX = 0;
  joyY = 0;
  lookTouchActive = false;
  private lookLastX = 0;
  private lookLastY = 0;

  sensitivity = 1;

  private canvas: HTMLCanvasElement;
  private isMobile: boolean;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.isMobile =
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 0 && window.matchMedia('(pointer: coarse)').matches);

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('pointerlockchange', this.onLockChange);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('click', this.onCanvasClick);
  }

  get mobile(): boolean {
    return this.isMobile;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('click', this.onCanvasClick);
  }

  /** フレーム末尾でエッジ入力をクリア */
  endFrame(): void {
    this.mouseDX = 0;
    this.mouseDY = 0;
    this.interactPressed = false;
    this.usePressed = false;
    this.pausePressed = false;
    this.inventoryPressed = false;
    this.hintPressed = false;
  }

  requestPointerLock(): void {
    if (this.isMobile) return;
    if (document.pointerLockElement !== this.canvas) {
      this.canvas.requestPointerLock();
    }
  }

  exitPointerLock(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const k = e.code;
    if (['Space', 'Tab'].includes(k)) e.preventDefault();
    if (this.keys.has(k)) return;
    this.keys.add(k);
    if (k === 'KeyE') this.interactPressed = true;
    if (k === 'KeyF') this.usePressed = true;
    if (k === 'Escape') this.pausePressed = true;
    if (k === 'KeyI') this.inventoryPressed = true;
    if (k === 'KeyH') this.hintPressed = true;
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private onLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement === this.canvas;
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.pointerLocked) return;
    this.mouseDX += e.movementX;
    this.mouseDY += e.movementY;
  };

  private onCanvasClick = (): void => {
    // pointer lock is requested by Game when playing
  };

  /** 移動ベクトル（ローカル: x=strafe, z=forward） */
  getMoveVector(): { x: number; z: number; run: boolean } {
    let x = 0;
    let z = 0;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) z -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) z += 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
    x += this.joyX;
    z += this.joyY;
    const len = Math.hypot(x, z);
    if (len > 1) {
      x /= len;
      z /= len;
    }
    const run = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    return { x, z, run };
  }

  isJump(): boolean {
    return this.keys.has('Space');
  }

  /** モバイル用API */
  setJoystick(x: number, y: number): void {
    this.joyX = x;
    this.joyY = y;
  }

  startLookTouch(x: number, y: number): void {
    this.lookTouchActive = true;
    this.lookLastX = x;
    this.lookLastY = y;
  }

  moveLookTouch(x: number, y: number): void {
    if (!this.lookTouchActive) return;
    this.mouseDX += x - this.lookLastX;
    this.mouseDY += y - this.lookLastY;
    this.lookLastX = x;
    this.lookLastY = y;
  }

  endLookTouch(): void {
    this.lookTouchActive = false;
  }

  pressInteract(): void {
    this.interactPressed = true;
  }

  pressUse(): void {
    this.usePressed = true;
  }

  pressPause(): void {
    this.pausePressed = true;
  }
}
